import { PARTY_ROLE_DISPLAY, type PartyRoleId } from '@steerfi/shared';
import { IconArrowLeft, IconChevronDown, IconChevronRight } from '@tabler/icons-react';
import type React from 'react';
import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { DeletePartnerDialog } from '@/components/partners/delete-partner-dialog';
import { EditPartnerModal } from '@/components/partners/edit-partner-modal';
import { PartnerStatusBadge } from '@/components/partners/partner-status-badge';
import { Button } from '@/components/ui/button';
import { useDocumentTitle } from '@/contexts/document-title-context';
import { useFocusReturn } from '@/hooks/use-focus-return';
import { usePartner } from '@/hooks/use-partners';
import { formatPartnerDate } from '@/schemas/partner-form-schema';

export const PartnerDetail: React.FC = () => {
  const { partyId } = useParams<{ partyId: string }>();
  const navigate = useNavigate();
  const { partner, isLoading, error, notFound, refetch } = usePartner(partyId ?? '');
  const [editOpen, setEditOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [metadataOpen, setMetadataOpen] = useState(false);
  const { capture: captureFocus, restore: restoreFocus } = useFocusReturn();

  useDocumentTitle(partner?.partner_name);

  if (isLoading) {
    return (
      <div className="flex flex-col gap-4 py-4 md:py-6 px-4 lg:px-6">
        <p className="text-sm text-muted-foreground">Loading partner…</p>
      </div>
    );
  }

  if (notFound) {
    return (
      <div className="flex flex-col gap-4 py-4 md:py-6 px-4 lg:px-6">
        <p className="text-sm text-muted-foreground">Partner not found.</p>
        <Link to="/partners" className="text-sm underline underline-offset-4">
          Back to Partners
        </Link>
      </div>
    );
  }

  if (error || !partner) {
    return (
      <div className="flex flex-col gap-4 py-4 md:py-6 px-4 lg:px-6">
        <div className="rounded-md border border-destructive/50 bg-destructive/10 p-4 text-destructive text-sm">
          {error ?? 'Failed to load partner.'}
        </div>
        <Link to="/partners" className="text-sm underline underline-offset-4">
          Back to Partners
        </Link>
      </div>
    );
  }

  const roleName =
    PARTY_ROLE_DISPLAY[partner.party_role_id as PartyRoleId] ?? String(partner.party_role_id);
  const hasMetadata = partner.metadata != null && Object.keys(partner.metadata).length > 0;

  return (
    <div className="flex flex-col gap-4 py-4 md:gap-6 md:py-6">
      <div className="px-4 lg:px-6">
        <Button
          variant="ghost"
          size="sm"
          className="gap-1.5 text-muted-foreground -ml-2 mb-2"
          onClick={() => navigate('/partners')}
        >
          <IconArrowLeft size={14} />
          Partners
        </Button>
        <h1 className="text-2xl font-bold tracking-tight">{partner.partner_name}</h1>
        <p className="text-sm text-muted-foreground mt-0.5 font-mono">{partner.partner_code}</p>
      </div>

      <div className="px-4 lg:px-6">
        <div className="rounded-md border">
          <div className="flex items-start justify-between p-5 border-b">
            <div>
              <p className="font-medium text-sm">Partner details</p>
              <p className="text-xs text-muted-foreground mt-0.5">
                Core attributes for this partner.
              </p>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  captureFocus();
                  setEditOpen(true);
                }}
              >
                Edit
              </Button>
              <Button
                variant="outline"
                size="sm"
                className="text-destructive border-destructive/40 hover:bg-destructive/10"
                onClick={() => {
                  captureFocus();
                  setDeleteOpen(true);
                }}
              >
                Delete
              </Button>
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-x-6 gap-y-5 p-5">
            <div>
              <p className="text-[11px] font-medium text-muted-foreground uppercase tracking-wide mb-1">
                Name
              </p>
              <p className="text-sm">{partner.partner_name}</p>
            </div>
            <div>
              <p className="text-[11px] font-medium text-muted-foreground uppercase tracking-wide mb-1">
                Code
              </p>
              <p className="text-sm font-mono">{partner.partner_code}</p>
            </div>
            <div>
              <p className="text-[11px] font-medium text-muted-foreground uppercase tracking-wide mb-1">
                Party role
              </p>
              <p className="text-sm">{roleName}</p>
            </div>
            <div>
              <p className="text-[11px] font-medium text-muted-foreground uppercase tracking-wide mb-1">
                Status
              </p>
              <PartnerStatusBadge statusId={partner.status_id} />
            </div>
            <div>
              <p className="text-[11px] font-medium text-muted-foreground uppercase tracking-wide mb-1">
                Created
              </p>
              <p className="text-sm">{formatPartnerDate(partner.created_at)}</p>
            </div>
            <div>
              <p className="text-[11px] font-medium text-muted-foreground uppercase tracking-wide mb-1">
                Updated
              </p>
              <p className="text-sm">{formatPartnerDate(partner.updated_at)}</p>
            </div>
          </div>
        </div>
      </div>

      {hasMetadata && (
        <div className="px-4 lg:px-6">
          <div className="rounded-md border">
            <button
              type="button"
              className="flex w-full items-center gap-2 p-5 text-left"
              onClick={() => setMetadataOpen((v) => !v)}
              aria-expanded={metadataOpen}
            >
              {metadataOpen ? (
                <IconChevronDown size={16} className="text-muted-foreground" />
              ) : (
                <IconChevronRight size={16} className="text-muted-foreground" />
              )}
              <span className="font-medium text-sm">Metadata</span>
            </button>
            {metadataOpen && (
              <div className="border-t p-5">
                <pre className="overflow-x-auto rounded-md bg-muted p-4 text-xs font-mono">
                  {JSON.stringify(partner.metadata, null, 2)}
                </pre>
              </div>
            )}
          </div>
        </div>
      )}

      <div className="px-4 lg:px-6">
        <div className="rounded-md border border-dashed p-5 text-center text-sm text-muted-foreground">
          More sections coming soon.
        </div>
      </div>

      <EditPartnerModal
        open={editOpen}
        partner={partner}
        onOpenChange={(open) => {
          if (!open) restoreFocus();
          setEditOpen(open);
        }}
        onSuccess={() => {
          setEditOpen(false);
          refetch();
        }}
      />
      <DeletePartnerDialog
        open={deleteOpen}
        partner={partner}
        onOpenChange={(open) => {
          if (!open) restoreFocus();
          setDeleteOpen(open);
        }}
        onSuccess={() => navigate('/partners')}
      />
    </div>
  );
};
